import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotificationItem } from "./NotificationItem";
import { Separator } from "@/components/ui/separator";
import { Bell, Inbox, Calendar, Clock } from "lucide-react";
import { format, isToday, isYesterday, isThisWeek } from "date-fns";
import type { Notification } from "@servify/shared-schema";

interface NotificationListProps {
  userId: string;
  userRole: 'admin' | 'employee';
  onClose?: () => void;
}

// Group notifications by time periods
const groupNotificationsByTime = (notifications: Notification[]) => {
  const groups: { [key: string]: Notification[] } = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: []
  };

  notifications.forEach((notification) => {
    const date = new Date(notification.createdAt || new Date());
    
    if (isToday(date)) {
      groups.today.push(notification);
    } else if (isYesterday(date)) {
      groups.yesterday.push(notification);
    } else if (isThisWeek(date)) {
      groups.thisWeek.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  // Sort each group by newest first
  Object.keys(groups).forEach(key => {
    groups[key].sort((a, b) => 
      new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
  });

  return groups;
};

const getGroupIcon = (groupKey: string) => {
  switch (groupKey) {
    case 'today': return <Clock className="h-4 w-4 text-servify-purple" />;
    case 'yesterday': return <Calendar className="h-4 w-4 text-servify-purple" />;
    case 'thisWeek': return <Calendar className="h-4 w-4 text-servify-green" />;
    case 'older': return <Inbox className="h-4 w-4 text-servify-medium-grey" />;
    default: return <Bell className="h-4 w-4 text-servify-medium-grey" />;
  }
};

const getGroupLabel = (groupKey: string) => {
  switch (groupKey) {
    case 'today': return 'Today';
    case 'yesterday': return 'Yesterday';
    case 'thisWeek': return 'This Week';
    case 'older': return 'Older';
    default: return groupKey;
  }
};

export function NotificationList({ userId, userRole, onClose }: NotificationListProps) {
  // Fetch only unread notifications (no read notifications in UI)
  // Poll more frequently since WebSocket might not be working
  const { data: notificationsData, isLoading, error: notificationsError } = useQuery({
    queryKey: ['/notifications/unread'],
    enabled: !!userId,
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    refetchInterval: 1000 * 30, // Poll every 30 seconds (more frequent since WebSocket may not work)
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: 2, // Retry failed requests
  });

  // Log errors for debugging
  useEffect(() => {
    if (notificationsError) {
      console.error('❌ Failed to fetch notifications:', notificationsError);
    }
  }, [notificationsError]);

  // Since we're using unread endpoint, all notifications are already unread
  const notifications = (notificationsData as { notifications?: Notification[] })?.notifications || [];
  const groupedNotifications = groupNotificationsByTime(notifications);
  const unreadCount = notifications.length;

  if (isLoading) {
    return (
      <div className="p-6 space-y-4" data-testid="notification-loading">
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 text-sm text-servify-medium-grey">
            <div className="h-4 w-4 border-2 border-servify-purple border-t-transparent rounded-full"></div>
            Loading your notifications...
          </div>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i}>
            <div className="flex items-start gap-4 p-4 bg-servify-white-grey/50 rounded-xl">
              <div className="h-10 w-10 bg-gradient-to-br from-servify-light-grey to-servify-medium-grey rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-servify-light-grey rounded-lg w-3/4"></div>
                <div className="h-3 bg-servify-light-grey rounded-lg w-1/2"></div>
                <div className="h-3 bg-servify-light-grey rounded-lg w-1/4"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div 
        className="p-12 text-center"
        data-testid="no-notifications"
      >
        <div className="space-y-6">
          {/* Animated empty state illustration */}
          <div className="relative mx-auto w-24 h-24">
            <div className="absolute inset-0 bg-gradient-to-br from-servify-purple/20 to-servify-purple/10 rounded-full"></div>
            <div className="absolute inset-3 bg-gradient-to-br from-servify-white-grey to-servify-purple/5 rounded-full flex items-center justify-center">
              <Bell className="h-8 w-8 text-servify-purple" />
            </div>
            {/* Static sparkles */}
            <div className="absolute -top-1 -right-1 h-3 w-3 bg-servify-green rounded-full"></div>
            <div className="absolute -bottom-1 -left-1 h-2 w-2 bg-servify-alt-green rounded-full"></div>
          </div>
          
          <div className="space-y-3">
            <h4 className="font-semibold text-lg text-servify-dark-grey">All caught up! 🎉</h4>
            <p className="text-sm text-servify-medium-grey max-w-xs mx-auto leading-relaxed">
              You're up to date with all your notifications. Updates will appear here as they come in.
            </p>
          </div>
          
          {/* Subtle background pattern */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-radial from-servify-purple/10 to-transparent rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[32rem] max-h-[70vh]">
      <div className="p-4 space-y-6" data-testid="notification-list">
        {/* Quick stats */}
        {unreadCount > 0 && (
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-servify-purple/5 to-servify-purple/10 rounded-lg border border-servify-purple/30">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-servify-purple rounded-full">
                <Bell className="h-3 w-3 text-servify-white-grey" />
              </div>
              <span className="text-sm font-medium text-servify-purple">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Grouped notifications */}
        {Object.entries(groupedNotifications).map(([groupKey, groupNotifications]) => {
          if (groupNotifications.length === 0) return null;
          
          return (
            <div key={groupKey} className="space-y-3">
              {/* Group header */}
              <div className="flex items-center gap-3 px-2">
                <div className="flex items-center gap-2">
                  {getGroupIcon(groupKey)}
                  <h3 className="font-semibold text-sm text-servify-dark-grey">
                    {getGroupLabel(groupKey)}
                  </h3>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-servify-light-grey via-transparent to-transparent"></div>
              </div>

              {/* Group notifications */}
              <div className="space-y-2">
                {groupNotifications.map((notification: Notification) => (
                  <div key={notification.id}>
                    <NotificationItem
                      notification={notification}
                      userRole={userRole}
                      onClose={onClose}
                    />
                  </div>
                ))}
              </div>

              {/* Separator between groups */}
              {groupKey !== 'older' && groupedNotifications.older.length > 0 && (
                <Separator className="my-4 bg-gradient-to-r from-transparent via-servify-light-grey to-transparent" />
              )}
            </div>
          );
        })}

        {/* Bottom padding */}
        <div className="h-2"></div>
      </div>
    </ScrollArea>
  );
}